import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ClientAudioAnalyzeService,
  ClientRuntimeUnavailableError,
  ProcessingAnalysisMode
} from './client-audio-analyze.service';
import { DriveService } from './drive.service';
import {
  ProcessingClientRenderPayload,
  ProcessingJobApiService,
  ProcessingRawUploadReservation,
  SubmitProcessingJobResult
} from './processing-job-api.service';
import { ProcessingRuntimeStatusReporter } from './processing-runtime-status-reporter.service';
import { ProcessingRenderFlowService } from '../../Rendering/non-editor/processing/processing-render-flow.service';

type StatusCallback = (message: string) => void;

@Injectable({ providedIn: 'root' })
export class ProcessingUploadFlowService {
  private readonly api = inject(ProcessingJobApiService);
  private readonly audioAnalyze = inject(ClientAudioAnalyzeService);
  private readonly driveService = inject(DriveService);
  private readonly runtimeRender = inject(ProcessingRenderFlowService);
  private readonly statusReporter = inject(ProcessingRuntimeStatusReporter);

  loadOptions() {
    return this.api.getUserProcessingOptions();
  }

  isRawFolder(folder: string | null | undefined): boolean {
    return ['root', 'raw'].includes((folder || '').trim().toLowerCase());
  }

  async upload(params: {
    file: File;
    currentFolder: string;
    options: any;
    analysisMode?: ProcessingAnalysisMode;
    onStatus?: StatusCallback;
    onJobId?: (jobId: string) => void;
  }): Promise<void> {
    if (!this.isRawFolder(params.currentFolder)) {
      params.onStatus?.('Uploading file...');
      await firstValueFrom(this.driveService.uploadFile(params.file, params.currentFolder));
      return;
    }

    await this.uploadAndProcessRaw(params.file, params.options, params.analysisMode || 'speech', params.onStatus, params.onJobId);
  }

  private async uploadAndProcessRaw(
    file: File,
    options: any,
    analysisMode: ProcessingAnalysisMode,
    onStatus?: StatusCallback,
    onJobId?: (jobId: string) => void
  ): Promise<void> {
    if (!options) {
      throw new Error('Processing options are still loading.');
    }

    let reservation: ProcessingRawUploadReservation | null = null;

    try {
      onStatus?.('Reserving canonical raw upload target...');
      reservation = await firstValueFrom(this.api.reserveRawUploadTarget({
        fileName: file.name,
        contentType: file.type || 'video/mp4',
        fileSizeBytes: file.size
      }));
      onJobId?.(reservation.jobId);

      onStatus?.('Uploading original video directly to storage. Do not close this window...');
      await this.api.uploadBlobToTarget(file, reservation.sourceUpload, file.type || 'video/mp4');

      onStatus?.('Writing raw metadata. Do not close this window...');
      await this.api.uploadBlobToTarget(
        this.rawMetadataBlob(file, reservation),
        reservation.metadataUpload,
        'application/json'
      );

      const clientAnalyze = await this.audioAnalyze.prepare(file, onStatus, reservation.jobId, analysisMode);
      if (!clientAnalyze) {
        throw new ClientRuntimeUnavailableError('Processing uploads require local runtime analysis before render planning.');
      }

      if (analysisMode === 'speech' && !clientAnalyze.hasAudio) {
        throw new ClientRuntimeUnavailableError('Processing uploads require local audio extraction before render planning.');
      }

      if (analysisMode === 'visual' && !this.hasFrameKeys(clientAnalyze.frameKeysJson)) {
        throw new ClientRuntimeUnavailableError('Visual-only Processing uploads require local frame rendering before render planning.');
      }

      onStatus?.(analysisMode === 'visual'
        ? 'Submitting Processing job with visual frames. Do not close this window...'
        : 'Submitting Processing job with temp audio. Do not close this window...');
      const submitResult = await firstValueFrom(this.api.submitProcessingJob({
        reservedJobId: reservation.jobId,
        fileName: file.name,
        filePath: reservation.sourceUpload.filePath,
        rawMetadataFilePath: reservation.metadataUpload.filePath,
        options,
        clientAnalyze
      }));

      const jobId = this.resultJobId(submitResult) || reservation.jobId;
      onJobId?.(jobId);
      const clientRender = this.extractClientRenderPayload(submitResult);
      if (!clientRender) {
        throw new Error('Processing upload returned no runtime render plan. Server render queues are disabled for uploads.');
      }

      const status = await firstValueFrom(this.api.getProcessingJobStatus(jobId));
      await this.runtimeRender.render({
        file,
        clientRender,
        options,
        fallbackJobId: jobId,
        checkpointResourcesJson: status.checkpointResourcesJson ?? null,
        onStatus
      });
    } catch (err) {
      await this.statusReporter.update(
        reservation?.jobId,
        'failed',
        0,
        'RuntimeFailed',
        err instanceof Error ? err.message : 'Processing failed after upload.'
      );
      throw err;
    }
  }

  private rawMetadataBlob(file: File, reservation: ProcessingRawUploadReservation): Blob {
    const payload = {
      targetId: reservation.targetId,
      jobId: reservation.jobId,
      jobIdShort: reservation.jobIdShort,
      uploadedAtUtc: new Date().toISOString(),
      source: {
        assetId: reservation.sourceUpload.assetId,
        fileName: file.name,
        filePath: reservation.sourceUpload.filePath,
        contentType: file.type || 'video/mp4',
        sizeBytes: file.size
      },
      metadata: {
        assetId: reservation.metadataUpload.assetId,
        filePath: reservation.metadataUpload.filePath
      }
    };

    return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  }

  private extractClientRenderPayload(result: SubmitProcessingJobResult): ProcessingClientRenderPayload | null {
    const mode = String(result?.mode ?? result?.Mode ?? '').trim().toLowerCase();
    const payload = result?.clientRender ?? result?.ClientRender;
    if (mode !== 'client-render' || !payload) return null;

    return {
      ...payload,
      jobId: String(payload.jobId ?? result?.jobId ?? result?.JobId ?? '')
    };
  }

  private resultJobId(result: SubmitProcessingJobResult): string | null {
    const value = result?.jobId ?? result?.JobId ?? result?.clientRender?.jobId ?? result?.ClientRender?.jobId;
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private hasFrameKeys(frameKeysJson?: string | null): boolean {
    if (!frameKeysJson?.trim()) return false;

    try {
      const value = JSON.parse(frameKeysJson);
      return Array.isArray(value) && value.some(item => typeof item === 'string' && item.trim().length > 0);
    } catch {
      return false;
    }
  }
}
